import api from "./api";

/** GET /business/branches */
export const fetchBranches = async (page = 1, perPage = 15) => {
  const { data } = await api.get("/business/branches", {
    params: { page, per_page: perPage },
  });
  return { branches: data.data.branches, meta: data.meta };
};

/**
 * POST /business/branches — multipart, because of the logo and media files.
 * `values.businessTypeIds` is an array of ids; `values.media` is a FileList/array.
 */
/**
 * The location fields shared by create and update. Optional ones are left out
 * entirely when blank rather than sent empty, so the API keeps its nulls.
 */
const appendLocation = (form, values) => {
  form.append("name", values.name);
  form.append("city_id", values.cityId);
  form.append("currency_id", values.currencyId);
  form.append("address", values.address);

  const optional = {
    code: values.code,
    address_line2: values.addressLine2,
    region: values.region,
    postal_code: values.postalCode,
    phone: values.phone,
    email: values.email,
    capacity: values.capacity,
    status: values.status,
    // Only chosen under a business serving both; a single-gender business
    // leaves it blank and the API sets the business's gender.
    gender: values.gender,
    // Left blank, the API falls back to the city's country timezone.
    timezone: values.timezone,
    description: values.description,
    latitude: values.latitude,
    longitude: values.longitude,
    // A pasted Google Maps link, for directions. Separate from the coordinates
    // above, which are what distance search runs on.
    map_url: values.mapUrl,
    // Optional: a place id or a Google link carrying one. Left out, the API
    // tries the map link instead and stays silent when it finds nothing.
    google_place_id: values.googlePlaceId,
  };

  Object.entries(optional).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) form.append(key, value);
  });
};

/**
 * Branch manager: either an existing business user, or a new account created
 * alongside the branch in one transaction. An "unchanged" mode — which only the
 * edit form offers — sends nothing at all, and the API keeps the manager the
 * branch already has.
 */
const appendManager = (form, manager) => {
  if (!manager || manager.managerMode === "unchanged") return;

  form.append("manager_mode", manager.managerMode);

  if (manager.managerMode === "existing") {
    form.append("user_id", manager.userId);
    return;
  }

  form.append("new_manager[name]", manager.newManager.name);
  form.append("new_manager[email]", manager.newManager.email);
  form.append("new_manager[password]", manager.newManager.password);
  form.append("new_manager[password_confirmation]", manager.newManager.passwordConfirmation);
};

/**
 * The week, flattened the way multipart needs it. A closed day sends no row at
 * all, so the API's "at least one open day" is simply a non-empty list.
 */
const appendWorkingHours = (form, rows) => {
  (rows ?? []).forEach((row, index) => {
    form.append(`working_hours[${index}][day]`, row.day);
    form.append(`working_hours[${index}][opens_at]`, row.opens_at);
    form.append(`working_hours[${index}][closes_at]`, row.closes_at);
  });
};

export const createBranch = async (values) => {
  const form = new FormData();

  appendLocation(form, values);
  // Required from file "hours + maplink" on: a branch is created with its week.
  appendWorkingHours(form, values.workingHours);

  if (values.logo) form.append("logo", values.logo);

  values.businessTypeIds.forEach((id) => form.append("business_type_ids[]", id));
  (values.media || []).forEach((file) => form.append("media[]", file));

  appendManager(form, values.manager);

  const { data } = await api.post("/business/branches", form);
  return data.data.branch;
};

/**
 * PUT /business/branches/{id} — sent as POST with _method=PUT because Laravel
 * does not parse multipart bodies on a real PUT.
 */
export const updateBranch = async (id, values) => {
  const form = new FormData();
  form.append("_method", "PUT");

  appendLocation(form, values);

  if (values.logo) form.append("logo", values.logo);
  // Left out entirely when the form has no types to send, which is how the API
  // is told to keep the ones the branch already carries.
  (values.businessTypeIds ?? []).forEach((typeId) => form.append("business_type_ids[]", typeId));
  appendManager(form, values.manager);

  const { data } = await api.post(`/business/branches/${id}`, form);
  return data.data.branch;
};

/**
 * POST /business/branches/map-link/resolve
 *
 * The server's cascade, for the links the browser cannot read on its own: a
 * short maps.app.goo.gl needs a redirect followed, and that is not something a
 * page can do cross-origin. Answers { lat, lng, source, success } and reports
 * "no" as success:false rather than as an error, so a failure here is never
 * worth showing anybody — the coordinate fields are simply left to be typed.
 */
export const resolveMapLink = async (mapUrl) => {
  const { data } = await api.post("/business/branches/map-link/resolve", { map_url: mapUrl });
  return data.data;
};

/**
 * GET /business/branches/usage — the plan caps how many branches are *active*
 * at once, not how many exist. `branches_active_limit` is null when unlimited.
 */
export const fetchBranchUsage = async () => {
  const { data } = await api.get("/business/branches/usage");
  return data.data;
};

/** Whether the plan has no room left for one more active branch. */
export const activeLimitReached = (usage) =>
  usage?.branches_active_limit != null && usage.branches_active_count >= usage.branches_active_limit;

/** GET /business/branches/{id} — one branch with city, currency, categories and media. */
export const fetchBranch = async (id) => {
  const { data } = await api.get(`/business/branches/${id}`);
  return data.data.branch;
};

/** DELETE /business/branches/{id} */
export const deleteBranch = (id) => api.delete(`/business/branches/${id}`);

/**
 * POST /business/branches/{id}/media — adds files without touching the branch's
 * details. Returns the branch's full media list.
 */
export const uploadBranchMedia = async (branchId, files) => {
  const form = new FormData();
  files.forEach((file) => form.append("media[]", file));

  const { data } = await api.post(`/business/branches/${branchId}/media`, form);
  return data.data.media;
};

/** DELETE /business/branches/{branchId}/media/{mediaId} */
export const deleteBranchMedia = (branchId, mediaId) =>
  api.delete(`/business/branches/${branchId}/media/${mediaId}`);

/** GET /business/branches/{id}/media — in the saved order, the cover flagged. */
export const fetchBranchMedia = async (branchId) => {
  const { data } = await api.get(`/business/branches/${branchId}/media`);
  return data.data.media;
};

/** PUT /business/branches/{id}/media/order — every media id, in the new order. */
export const reorderBranchMedia = async (branchId, ids) => {
  const { data } = await api.put(`/business/branches/${branchId}/media/order`, { ids });
  return data.data.media;
};

/** POST /business/branches/{id}/media/{mediaId}/primary — the branch's cover photo. */
export const setPrimaryBranchMedia = async (branchId, mediaId) => {
  const { data } = await api.post(`/business/branches/${branchId}/media/${mediaId}/primary`);
  return data.data.media;
};

/** GET /business/branches/{id}/working-hours */
export const fetchWorkingHours = async (branchId) => {
  const { data } = await api.get(`/business/branches/${branchId}/working-hours`);
  return data.data.working_hours;
};

/**
 * PUT /business/branches/{id}/working-hours — replaces the whole week.
 * `rows` is [{ day, opens_at, closes_at }].
 */
export const saveWorkingHours = async (branchId, rows) => {
  const { data } = await api.put(`/business/branches/${branchId}/working-hours`, {
    working_hours: rows,
  });
  return data.data.working_hours;
};

/* ------------------------------------------------- closures & special dates */

/** GET /business/branches/{id}/closures — upcoming by default. */
export const fetchClosures = async (branchId, { past = false } = {}) => {
  const { data } = await api.get(`/business/branches/${branchId}/closures`, {
    params: past ? { past: 1 } : {},
  });
  return data.data.closures;
};

/** The shape both writes send: a closed day carries no hours. */
const closurePayload = (values) => ({
  date: values.date,
  is_closed: Boolean(values.isClosed),
  reason: values.reason || null,
  ...(values.isClosed ? {} : { opens_at: values.opensAt, closes_at: values.closesAt }),
});

export const createClosure = async (branchId, values) => {
  const { data } = await api.post(`/business/branches/${branchId}/closures`, closurePayload(values));
  return data.data.closure;
};

export const updateClosure = async (branchId, closureId, values) => {
  const { data } = await api.put(
    `/business/branches/${branchId}/closures/${closureId}`,
    closurePayload(values)
  );
  return data.data.closure;
};

export const deleteClosure = (branchId, closureId) =>
  api.delete(`/business/branches/${branchId}/closures/${closureId}`);
